import { useQueries, useQuery } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { Link, useParams, useSearchParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { LayoutHeader } from "views/applications-id/components/LayoutHeader";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ActivityLog } from "../../shared/app/ActivityLog";
import { DecisionConfirmationModal } from "views/applications-id/components/DecisionConfirmationModal";
import { Application, Applicant } from "types/application";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { ApplicationScoreCard } from "./components/ApplicationScoreCard";
import {
  ApplicationStatus,
  ExecutionModes,
  VerificationNames
} from "shared/types";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Template } from "types/landlord/verificationTemplate";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { Loader } from "shared/components/Loader";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import {
  generateQueries,
  getIncomeGraphsData
} from "./components/IncomeSummary/utils";
import { VerificationData } from "./components/IncomeSummary/types";
import {
  ApplicantStatus,
  ApplicantType,
  DecisionAction
} from "shared/types/application";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { ApplicationMonthlyRentCard } from "views/applications-id/components/ApplicationMonthlyRentCard";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { Card } from "shared/components/Card";
import { cx } from "shared/utils/classnames";
import { GroupStats } from "./components/GroupStats";
import { RecommendationButton } from "./components/RecommendationButton";
import { FaceID, CursorClick01, Loading02 } from "untitledui-js-base";
import { CreditReport } from "types/landlord/reports";
import { Tabs } from "shared/components/Tabs";
import { ApplicantsTable } from "./components/ApplicantsTable";
import { MoveModal } from "../../shared/app/MoveModal";
import { RefundModal } from "shared/app/RefundModal";
import { ExplainRecommendationModal } from "shared/app/ExplainRecommendationModal";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import { canDecisionBeMade } from "utils/canMakeDecision";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { hasActionInManualApplication } from "./helpers";
import { UncancelModal } from "shared/app/UncancelModal";
import { NoRecommendationCard } from "./components/NoRecommendationCard";

const IncomeSummary = lazy(() => import("./components/IncomeSummary"));
type TabNames = "Applicants" | "Guarantors" | "Occupants";

export const ApplicationsIdOverview = () => {
  const [_, setSearchParams] = useSearchParams();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showActivityLog, setShowActivityLog] = useState<boolean>(false);
  const { applicationId } = useParams();
  const [showDecisionFlowModal, setShowDecisionFlowModal] =
    useState<boolean>(false);
  const [showExplainRecommendations, setShowExplainRecommendations] =
    useState<boolean>(false);
  const [showDecisionConfirmationModal, setDecisionConfirmationModal] =
    useState<boolean>(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);
  const [hideApplicantNamesAndUnitInfo, setHideApplicantNamesAndUnitInfo] =
    useState(false);

  const [previewTemplate, setPreviewTemplate] = useState<string>();

  const [tab, setActiveTab] = useState<TabNames>("Applicants");
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const query = useQuery<Application>({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(applicationId || "")
  });

  const { type, permissions } = useAuthContext();

  const {
    unit,
    property,
    applicants,
    status,
    submitted_at,
    verification_template,
    pending_magic_links,
    monthly_rent_amount,
    pets_count,
    pet_rent_value,
    has_voucher,
    voucher_amount,
    include_co_signer_in_criteria_calculation,
    has_pending_verifications,
    requires_overrides_identity_verification,
    decision_at
  } = query.data || {};

  const scoreBased =
      verification_template?.recommendation_method === "score_based",
    ruleBased = verification_template?.recommendation_method === "rule_based",
    noRecommendationEnabled = !scoreBased && !ruleBased;

  const [applicantUsers, coSignerUsers, occupantUsers] = useMemo(() => {
    return (applicants || []).reduce<Applicant[][]>(
      (acc, applicant) => {
        switch (applicant.role) {
          case ApplicantType.CoSigner:
            acc[1].push(applicant);
            break;
          case ApplicantType.Occupant:
            acc[2].push(applicant);
            break;
          default:
            acc[0].push(applicant);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [applicants]);

  const [applicantLinks, coSignerLinks, occupantLinks] = useMemo(() => {
    return (pending_magic_links || []).reduce<
      { email: string; phone: string | null; role: string }[][]
    >(
      (acc, link) => {
        switch (link.role) {
          case ApplicantType.CoSigner:
            acc[1].push(link);
            break;
          case ApplicantType.Occupant:
            acc[2].push(link);
            break;
          default:
            acc[0].push(link);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [pending_magic_links]);

  const rolesEnabledInSettings = useMemo(() => {
    if (!verification_template?.settings) return [];

    return Object.keys(verification_template.settings).filter(
      (role) => verification_template.settings[role].enabled_screening_services
    ) as ApplicantType[];
  }, [verification_template]);

  const incomeQueries = useMemo(
    () =>
      generateQueries(
        applicants?.filter((appl) => {
          if (!include_co_signer_in_criteria_calculation)
            return (
              appl.role === ApplicantType.Applicant &&
              appl.application_status === ApplicantStatus.Submitted
            );
          else return appl.application_status === ApplicantStatus.Submitted;
        }) as Applicant[],
        applicationId
      ),
    [applicants, applicationId, include_co_signer_in_criteria_calculation]
  );

  const combinedQueryMeta = useQueries({
    queries: incomeQueries || [],

    combine: (results) => {
      return {
        verifiedIncomeData: results.reduce((acc, result) => {
          if (
            result.data &&
            result.data.verification?.name ===
              VerificationNames.VerifiedIncomeVerification
          )
            acc.push(result.data as VerificationData);

          return acc;
        }, [] as VerificationData[]),
        selfAttestedIncomeData: results.reduce((acc, result) => {
          if (
            result.data &&
            result.data.verification?.name ===
              VerificationNames.IncomeVerification
          )
            acc.push(result.data as VerificationData);

          return acc;
        }, [] as VerificationData[]),
        creditScore: results.reduce((acc, result) => {
          if (
            result.data &&
            result.data.verification?.name === VerificationNames.CreditReport
          )
            acc.push(result.data as VerificationData);

          return acc;
        }, [] as VerificationData[]),
        isError: results.some((result) => result.isError),
        isLoading: results.some((result) => result.isLoading)
      };
    }
  });

  const {
    verifiedIncomeGraphData,
    pastThreeMonthsAvgTotal,
    totalSelfAttestedCurrentIncome,
    netIncomeToRentRatio,
    selfAttestedIncomeToRentRatio,
    totalVerifiedNet,
    totalDebtToNetIncome
  } = useMemo(
    () =>
      getIncomeGraphsData(
        combinedQueryMeta.verifiedIncomeData,
        combinedQueryMeta.selfAttestedIncomeData,
        (monthly_rent_amount?.cents || 0) / 100
      ),
    [combinedQueryMeta, monthly_rent_amount]
  );

  const averageCreditScore = useMemo(() => {
    const validCredits = combinedQueryMeta.creditScore.filter((credit) => {
      const score = (credit.verification?.report as CreditReport | undefined)
        ?.score;
      return (
        score !== undefined && score !== null && score !== 0 && score !== 4
      );
    });

    const total = validCredits.reduce((acc, credit) => {
      const score =
        (credit.verification?.report as CreditReport | undefined)?.score || 0;
      return acc + score;
    }, 0);

    return validCredits.length ? Math.round(total / validCredits.length) : 0;
  }, [combinedQueryMeta.creditScore]);

  const noHitScoresCount = useMemo(() => {
    return combinedQueryMeta.creditScore.filter((credit) => {
      const score = (credit.verification?.report as CreditReport | undefined)
        ?.score;
      return score == 4;
    }).length;
  }, [combinedQueryMeta.creditScore]);

  const [lowestCreditScore, highestCreditScore] = useMemo(() => {
    const validCredits = combinedQueryMeta.creditScore.filter((credit) => {
      const score = (credit.verification?.report as CreditReport | undefined)
        ?.score;
      return (
        score !== undefined && score !== null && score !== 0 && score !== 4
      );
    });

    const sortedScores = validCredits
      .map((credit) => {
        return (
          (credit.verification?.report as CreditReport | undefined)?.score || 0
        );
      })
      .sort((a, b) => a - b);

    return [sortedScores[0], sortedScores[sortedScores.length - 1]];
  }, [combinedQueryMeta.creditScore]);

  const totelManualVerifications = useMemo(() => {
    return (
      query.data?.requires_manual_or_conditional_execution?.applicants?.reduce(
        (count, applicant) => {
          const manualVerificationsCount = applicant.verifications.filter(
            (verification) =>
              verification.execution_mode === ExecutionModes.Manual
          ).length;

          return count + manualVerificationsCount;
        },
        0
      ) || 0
    );
  }, [query.data?.requires_manual_or_conditional_execution]);

  const canMakeDecision = useMemo(() => {
    return canDecisionBeMade({
      applicants: applicants || [],
      status: status || ApplicationStatus.Started,
      type,
      permissions,
      requirements: query.data?.make_decision_requirements
    });
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions
  ]);

  const availableTabs: TabNames[] = useMemo(() => {
    const res: TabNames[] = [];
    if (applicantLinks.length + applicantUsers.length > 0)
      res.push("Applicants");
    if (coSignerLinks.length + coSignerUsers.length > 0) res.push("Guarantors");
    if (occupantLinks.length + occupantUsers.length > 0) res.push("Occupants");

    return res.length > 0 ? res : [];
  }, [
    coSignerLinks,
    coSignerUsers,
    occupantLinks,
    occupantUsers,
    applicantLinks,
    applicantUsers
  ]);

  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(tab))
      setActiveTab(availableTabs[0]);
  }, [availableTabs]);

  const notCancelledApplicants = useMemo(() => {
    return applicants?.filter(
      (applicant) => applicant.application_status !== ApplicantStatus.Canceled
    );
  }, [applicants]);

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (query.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading application
          </h1>

          <Link to="/applications">
            <Button>Go back</Button>
          </Link>
        </div>
      </Layout>
    );

  // handle loading state here
  if (!query.data || query.isLoading)
    return (
      <Layout>
        <div className="mx-auto h-full flex flex-col items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );
  if (!unit || !property) return null;

  const allowCoSigner = Boolean(
      // INFO: According to Dat it was true because before this field existed, we used to allow cosigners by default
      verification_template?.settings.co_signer.enabled_screening_services ??
        true
    ),
    allowOccupant = Boolean(
      verification_template?.settings.occupant?.enabled_screening_services ??
        false
    );

  const manualApplicantsInProgress = applicants?.filter((appl) =>
    hasActionInManualApplication(appl as Applicant)
  );

  return (
    <Layout
      sectionProps={{
        // Hide applicants' names and unit info in header when scroll down
        onScroll: (e) => {
          setHideApplicantNamesAndUnitInfo(e.currentTarget.scrollTop > 0);
        }
      }}
      sectionClassName="!p-0"
      headerClassName="z-[2] bg-white border-b border-alpha-100"
      header={
        <LayoutHeader
          hideApplicantNamesAndUnitInfo={hideApplicantNamesAndUnitInfo}
          isRuleBased={
            verification_template?.recommendation_method === "rule_based"
          }
          disableDecisionFlow={!canMakeDecision}
          unit={unit}
          property={property}
          applicants={applicants}
          invitees={pending_magic_links || []}
          applicationId={applicationId || ""}
          desiredMoveInDate={query.data.desired_move_in_date}
          viewTemplate={() => {
            setPreviewTemplate(verification_template?.id);
          }}
          requirements={query.data.make_decision_requirements}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          applicationStatus={status || ApplicationStatus.Started}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          submittedAt={submitted_at || ""}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          onCancelClick={() => setShowCancelModal(true)}
          onUncancelClick={() => setShowUncancelModal(true)}
          onRefundClick={() => setShowRefundModal(true)}
          includeCoSignerInCriteriaCalculation={
            include_co_signer_in_criteria_calculation
          }
          onMoveClick={() => setShowMoveModal(true)}
          decisionAt={decision_at || ""}
        />
      }
    >
      <div className="space-y-6 p-6 pb-0">
        <ApplicationMonthlyRentCard
          monthly_rent_amount={unit.monthly_rent_amount}
          pets_count={pets_count}
          pet_rent_value={pet_rent_value}
          has_voucher={has_voucher}
          voucher_amount={voucher_amount}
        />
        <Show
          if={
            !Boolean(
              requires_overrides_identity_verification?.applicants.length
            ) &&
            [
              ApplicationStatus.Submitted,
              ApplicationStatus.Approved,
              ApplicationStatus.Rejected,
              ApplicationStatus.ConditionalApproved,
              ApplicationStatus.Canceled
            ].includes(status as ApplicationStatus)
          }
        >
          <div className="flex gap-4 flex-wrap md:flex-nowrap">
            <Show if={totelManualVerifications > 0}>
              <Card className="flex flex-col min-w-[300px]">
                <div className="flex flex-grow items-center justify-center bg-primary-20 border-b border-alpha-40 py-[52px]">
                  <CursorClick01 className="stroke-primary-500" size="56" />
                </div>

                <div className="p-6 flex flex-col items-center justify-center gap-2">
                  <p className="font-bold text-base text-content-1000">
                    {totelManualVerifications} reports require manual run
                  </p>
                  <Show if={scoreBased || ruleBased}>
                    <p className="text-content-400 font-medium text-xs text-center">
                      Decision will be recommended after all reports have been
                      run
                    </p>
                  </Show>
                </div>
              </Card>
            </Show>

            <Show
              if={
                scoreBased &&
                query.data.score != null &&
                !Boolean(has_pending_verifications)
              }
            >
              <ApplicationScoreCard
                applicationScore={query.data.score}
                recommendation={query.data.recommendation}
                onButtonClick={() => setShowDecisionFlowModal(true)}
                criteriaCheckSettings={query.data.criteria_check_settings}
                applicationSubmitted={status === ApplicationStatus.Submitted}
                templateCriteriaSettings={
                  verification_template?.screening_criteria
                }
                applicants={applicants}
                requires_overrides_identity_verification={
                  requires_overrides_identity_verification
                }
                status={status}
              />
            </Show>

            <Show
              if={
                ruleBased &&
                Boolean(query.data.recommendation) &&
                !Boolean(has_pending_verifications)
              }
            >
              <div className="max-w-[300px] w-full shrink">
                <Card className="h-full">
                  <div
                    className={cx(
                      "p-6 pb-4 w-full flex flex-col items-center justify-center rounded-t-lg gap-4",
                      query.data.recommendation === DecisionAction.Decline
                        ? "bg-danger-50"
                        : query.data.recommendation === DecisionAction.Approve
                          ? "bg-success-50"
                          : "bg-warning-50"
                    )}
                  >
                    <RuleBaseGraph
                      className="h-16 w-16"
                      recommendation={query.data.recommendation}
                    />
                    {verification_template?.recommendation_method ===
                      "rule_based" && (
                      <p
                        className={cx(
                          "text-base font-medium",
                          query.data.recommendation === DecisionAction.Decline
                            ? "text-danger-500"
                            : query.data.recommendation ===
                                DecisionAction.Approve
                              ? "text-success-700"
                              : "text-warning-700"
                        )}
                      >
                        {query.data.auto_approved
                          ? "Automatically approved"
                          : "Recommend"}{" "}
                        {query.data.auto_approved
                          ? ""
                          : query.data.recommendation === DecisionAction.Approve
                            ? "approval"
                            : query.data.recommendation ===
                                DecisionAction.Decline
                              ? "declining"
                              : "conditional approval"}
                      </p>
                    )}
                  </div>
                  <div className="p-6 flex items-center w-full mt-auto px-6 border-t border-alpha-40">
                    {verification_template?.recommendation_method !==
                      "rule_based" && (
                      <p className="text-xs text-content-300 font-medium text-center mb-4">
                        Based on your criteria, we recommend{" "}
                        {query.data.recommendation === DecisionAction.Approve
                          ? "approval"
                          : query.data.recommendation === DecisionAction.Decline
                            ? "declining"
                            : "conditional approval"}
                      </p>
                    )}

                    <RecommendationButton
                      onClickMakeDecision={() => setShowDecisionFlowModal(true)}
                      onClickExplainRecommendation={() =>
                        setShowExplainRecommendations(true)
                      }
                      canMakeDecision={!canDecisionBeMade}
                      recommendation={query.data.recommendation!}
                      hasReasons={Boolean(query.data.rule_reasons)}
                      status={status}
                      autoApproved={query.data.auto_approved}
                    />
                  </div>
                </Card>
              </div>
            </Show>

            <Show
              if={
                noRecommendationEnabled && !Boolean(has_pending_verifications)
              }
            >
              <NoRecommendationCard />
            </Show>

            <GroupStats
              criteriaTypeOptions={
                query.data.criteria_check_settings?.credit_score?.type ||
                CriteriaTypeOptions.AVERAGE
              }
              highestCreditScore={highestCreditScore}
              lowestCreditScore={lowestCreditScore}
              noHitCount={noHitScoresCount}
              totalVerifiedNet={totalVerifiedNet}
              creditScoreAvg={averageCreditScore}
              pastThreeMonthsAvgTotal={pastThreeMonthsAvgTotal}
              totalDebtToNetIncome={totalDebtToNetIncome}
              totalSelfAttestedCurrentIncome={totalSelfAttestedCurrentIncome}
              netIncomeToRentRatio={netIncomeToRentRatio}
              selfAttestedIncomeToRentRatio={selfAttestedIncomeToRentRatio}
              criteriaSettings={
                verification_template?.screening_criteria?.applicant_settings
              }
              recommendationMethod={
                verification_template?.recommendation_method
              }
              groupStat={query.data.group_stat}
            />
          </div>

          <Suspense
            fallback={
              <Card className="flex items-center justify-center gap-2 h-[106px] mb-6 px-4 bg-white">
                <Loader />
              </Card>
            }
          >
            <IncomeSummary
              verifiedIncomeGraphData={verifiedIncomeGraphData}
              isLoading={combinedQueryMeta.isLoading}
              isError={combinedQueryMeta.isError}
            />
          </Suspense>
        </Show>
        <Show
          if={Boolean(
            requires_overrides_identity_verification?.applicants.length
          )}
        >
          <Card className="flex items-center gap-2 py-6 px-4">
            <div className="relative m-4">
              <FaceID
                className="size-10 shrink-0 stroke-content-200"
                strokeWidth={1.5}
              />
              <InfoIcon className="absolute -bottom-1.5 -right-1.5 size-6 text-danger-500 bg-white rounded-full" />
            </div>
            <div className="space-y-2">
              <h2 className="text-base font-bold">
                {`Identity verification failed for ${requires_overrides_identity_verification?.applicants
                  .map((applicant) => applicant.name)
                  .join(", ")}`}
              </h2>
              <p className="text-sm">
                You can manually override a failed identity verification run the
                remaining screening services
              </p>
            </div>
          </Card>
        </Show>
        <Show
          if={Boolean(
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              ApplicationStatus.Processing,
              undefined
            ].includes(status)
          )}
        >
          {manualApplicantsInProgress?.length ? (
            <Card className="h-[110px] mb-6 px-6 py-8 flex gap-6 items-center">
              <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center bg-primary-50">
                <span className="text-base text-primary-500 font-bold">
                  {manualApplicantsInProgress.length}
                </span>
              </div>
              <div>
                <h2 className="text-base font-medium text-content-1000 mb-1">
                  Application{manualApplicantsInProgress.length > 1 ? "s" : ""}{" "}
                  require manual completion{" "}
                </h2>
                <p className="text-sm text-content-300">
                  All in-progress applications must be completed in order to
                  make a decision
                </p>
              </div>

              <Button
                variant={ButtonVariant.PRIMARY}
                className="ml-auto"
                size={ButtonSize.SM}
                onClick={() => {
                  setSearchParams({
                    applicantId: manualApplicantsInProgress[0].id.toString()
                  });
                }}
              >
                Complete application
              </Button>
            </Card>
          ) : (
            <Card className="h-[110px] mb-6 px-6 py-8 flex gap-6 items-center">
              <Loading02
                strokeWidth={1}
                className="h-12 w-12 shrink-0 stroke-content-300"
              />
              <div>
                <h2 className="text-base font-medium text-content-1000 mb-1">
                  Application is in progress
                </h2>
                <p className="text-sm text-content-300">
                  All in-progress applications must be completed in order to
                  make a decision
                </p>
              </div>
            </Card>
          )}
        </Show>

        <Show if={availableTabs.length > 0}>
          <div className="mb-4">
            <Tabs
              tabs={availableTabs}
              displayMap={{
                Applicants: (
                  <>
                    Applicants ({applicantLinks.length + applicantUsers.length})
                  </>
                ),
                Guarantors: (
                  <>
                    Guarantors ({coSignerLinks.length + coSignerUsers.length})
                  </>
                ),
                Occupants: (
                  <>Occupants ({occupantLinks.length + occupantUsers.length})</>
                )
              }}
              selected={tab}
              setActiveTab={(tab: TabNames) => setActiveTab(tab)}
              variant="underline"
            />
          </div>
        </Show>
        <Show if={tab === "Applicants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            key="Applicants"
            applicationApplicants={notCancelledApplicants || []}
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            scoreBased={scoreBased}
            applicationId={applicationId}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Applicant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(applicantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (applicantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
        <Show if={tab === "Guarantors"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Guarantors"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.CoSigner
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(coSignerUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (coSignerLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>

        <Show if={tab === "Occupants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Occupants"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Occupant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(occupantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (occupantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
      </div>
      <TemplateReviewModal
        opened={!!previewTemplate}
        onClose={() => setPreviewTemplate(undefined)}
        template={verification_template as unknown as Template}
      />
      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={applicationId || ""}
        />
      </Show>
      <Show if={showDecisionFlowModal}>
        <MakeDecisionModal
          opened={showDecisionFlowModal}
          onClose={() => setShowDecisionFlowModal(false)}
          applicationId={applicationId || ""}
        />
      </Show>
      <ExplainRecommendationModal
        application={query.data}
        opened={showExplainRecommendations}
        onClose={() => setShowExplainRecommendations(false)}
      />
      <DecisionConfirmationModal
        opened={showDecisionConfirmationModal}
        onContinue={() => {
          setDecisionConfirmationModal(false);
          setShowDecisionFlowModal(true);
        }}
        onClose={() => setDecisionConfirmationModal(false)}
      />
      <InviteAndCreateModal
        // applicantsNames={[
        //   ...(applicants?.map(
        //     (applicant) => applicant.first_name + " " + applicant.last_name
        //   ) || []),
        //   ...(pending_magic_links?.map((link) => link.email) || [])
        // ].join(", ")}
        disabledRoles={
          [...(applicants || []), ...(pending_magic_links || [])].some(
            (applicant) =>
              [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                applicant.role as ApplicantType
              )
          )
            ? []
            : [ApplicantType.CoSigner]
        }
        applicationId={Number(applicationId)}
        open={showInviteApplicantsModal}
        setOpen={() => setShowInviteApplicantsModal(false)}
        propertyId={property.id}
        unitId={unit.id}
        onInviteRefreshKey={["APPLICATION", applicationId]}
      />
      <CancelOrRefundModal
        applicants={applicants}
        applicationId={showCancelModal ? String(applicationId) : ""}
        onClose={() => setShowCancelModal(false)}
        onSuccess={() => {
          query.refetch();
        }}
      />
      <UncancelModal
        applicationId={showUncancelModal ? String(applicationId) : ""}
        onClose={() => setShowUncancelModal(false)}
        onSuccess={() => {
          query.refetch();
        }}
      />
      <RefundModal
        applicationId={showRefundModal ? String(applicationId) : ""}
        onClose={() => setShowRefundModal(false)}
        queryKey={["APPLICATION", applicationId]}
      />
      <MoveModal
        applicationId={showMoveModal ? String(applicationId) : ""}
        onClose={() => setShowMoveModal(false)}
      />
    </Layout>
  );
};
